/**
 * Who builds and runs a service, for the Apps tab's "How it runs" picker: the builders vfs-service lists for each
 * service (`GET /runnables` › `builders`, vfs-service `src/preview/builders.js`): the detected code stack with its npm
 * scripts or Python entry files, `go run .`, the Makefile with its targets, each Dockerfile and compose file, and the
 * one that runs when nothing is chosen (`recommended`). The choice is saved in the service's `.flow/service.json`, in
 * the preview-runner's own fields (container-live/preview-runner `crates/driver-trait/src/launch.rs`):
 *
 * - the stack its own way (Vite's dev server, uvicorn, …): no `run` or `start` (`run: "stack"` when a compose file
 *   would otherwise run as the app);
 * - an npm script: `start: "npm run dev"` (`npm start`); a Python file: `start: "python3 server.py"`; Go: `go run .`;
 * - a Makefile target: `start: "make dev"` (`make` is in the runner's image; the target is checked: no shell);
 * - a Dockerfile: `run: "docker"` + `dockerfile`; a compose file: `run: "compose"` + `compose`, and its preview entry
 *   point `entry: { service, port }` (Apps' "Preview entry point"; the runner still reads a service's name alone);
 * - `port`: the port the app listens on when it does not take `PORT` (for a Dockerfile, the container's port);
 * - a command-line tool ("Command-line tool", utils/cliServices.js): `kind: "worker"`, the stack's `build` and an idle
 *   `start` (CLI_START): the runner builds it and keeps it, it never serves; Preview › Try it runs it;
 * - a native device app ("iOS app (Swift)", "Android app (Kotlin)", utils/deviceApps.js): `run: "device"`, nothing
 *   else: it never runs in the sandbox, Apps › Build for devices builds it (the one choice, read-only);
 * - a custom command (any `start` the picker did not write, or one typed in "Custom command"): `start` as typed, run by
 *   the runner with `sh -c` in the service folder, plus its own `install` and `build` steps (also `sh -c`) and `port`.
 *
 * The other fields of the file (`install`, `build`, `watch`, `mcp`, …) are kept (a custom command sets `install` and
 * `build` itself).
 */

import { CLI_START, isCliSpec } from './cliServices';

/** A make target, npm script or file name written into a command: no shell. */
export const SAFE_NAME = /^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/;
const NODE_STACKS = ['node', 'nextjs', 'angular', 'react-native'];
/** The fields of service.json the picker owns. */
const MANAGED = ['run', 'start', 'dockerfile', 'context', 'entry', 'port'];
/** The kinds whose options take a port of their own (`port` in service.json). */
const PORT_KINDS = ['node', 'python', 'go', 'make', 'dockerfile'];

const npmCommand = (name) => (name === 'start' ? 'npm start' : `npm run ${name}`);
const commandOf = (value) => (Array.isArray(value) ? value.join(' ') : String(value || '')).trim();

/** The spec without its run settings: the picker's fields, and a command-line tool's own (`kind`, `build`). */
function withoutRunSettings(spec) {
  const next = { ...(spec || {}) };
  MANAGED.forEach((key) => { delete next[key]; });
  if (spec?.run === 'compose') { delete next.compose; }
  if (isCliSpec(spec)) {
    delete next.kind;
    delete next.build;
  }
  return next;
}

/** The longest command "Custom command" takes (start, install or build): one line of a shell command. */
export const MAX_COMMAND_LENGTH = 500;

/**
 * Why a typed command cannot be saved, or null: not empty (unless `optional`), one line, at most MAX_COMMAND_LENGTH
 * characters. Nothing else: the runner runs it as typed with `sh -c` (preview-runner launch.rs), as it does a
 * hand-written `start`.
 */
export function commandProblem(text, { optional = false, what = 'the command that starts it' } = {}) {
  const value = String(text ?? '');
  if (!value.trim()) { return optional ? null : `Enter ${what}`; }
  if (/[\r\n]/.test(value)) { return 'One line only (join steps with && or put them in a script)'; }
  if (value.trim().length > MAX_COMMAND_LENGTH) { return `At most ${MAX_COMMAND_LENGTH} characters`; }
  return null;
}

/** Why a port cannot be saved, or null (empty: the app takes PORT). */
export function portProblem(port) {
  if (port === null || port === undefined || port === '') { return null; }
  const value = Number(port);
  return Number.isInteger(value) && value >= 1 && value <= 65535 ? null : 'A port is a number from 1 to 65535';
}

/**
 * The service.json after "Custom command" (`{ command, port, install, build }`): `start` as typed (trimmed), `port`
 * when set, `install` and `build` when set (else removed); `run`, a compose file run as the app and the picker's other
 * fields go, the rest (`compose` of dependencies, `watch`, `mcp`, …) is kept. Throws when a field cannot be saved.
 */
export function specForCustom(spec, { command, port = null, install = '', build = '' }) {
  const problem = commandProblem(command) || commandProblem(install, { optional: true }) || commandProblem(build, { optional: true }) || portProblem(port);
  if (problem) { throw new Error(problem); }
  const next = withoutRunSettings(spec);
  next.start = String(command).trim();
  if (Number(port) > 0) { next.port = Number(port); }
  delete next.install;
  delete next.build;
  if (String(install || '').trim()) { next.install = String(install).trim(); }
  if (String(build || '').trim()) { next.build = String(build).trim(); }
  return next;
}

/**
 * Commands that may start the service, from its builders (vfs-service): its make targets, npm scripts, Python entry
 * files and `go run .`, likely ones first (as vfs-service orders them), at most `limit`. The Dockerfile and compose
 * files are builders of their own (one click), not commands.
 */
export function commandSuggestions(info, limit = 6) {
  const out = [];
  (info?.builders || []).filter((builder) => ['make', 'node', 'python', 'go'].includes(builder.kind)).forEach((builder) => {
    (builder.options || []).forEach((item) => {
      if (!item.value) { return; }
      try {
        const start = startOf(builder.kind, item.value);
        if (start && !out.includes(start)) { out.push(start); }
      } catch { /* a name that cannot be run */ }
    });
  });
  // A Makefile's run target first: it is what a folder with nothing detected usually means.
  return out.sort((left, right) => Number(right.startsWith('make ')) - Number(left.startsWith('make '))).slice(0, limit);
}

const NO_STACK = /No supported stack detected(?![:\w])/;

/** Whether a build error is the runner's "No supported stack detected" (a folder it cannot start by itself). */
export const isNoStackError = (text) => NO_STACK.test(String(text || ''));

/**
 * The runner's "No supported stack detected" without what to do with .flow/service.json (Flow offers "Set how it
 * starts" instead): which stacks it knows, and that a Dockerfile needs Docker. A broken service.json stays as is.
 */
export function noStackSummary(text) {
  const whole = String(text || '').trim();
  const at = whole.search(NO_STACK);
  // Its own words from there on (a "could not start:" before it is the caller's).
  const message = at >= 0 ? whole.slice(at) : whole;
  if (at < 0 || /^No supported stack detected, and /.test(message)) { return message; }
  const supported = message.match(/Supported stacks: (.*?)\.(?=\s+(?:Choose|Otherwise)\b|\s*$)/);
  const list = supported ? supported[1] : null;
  const dockerfile = /This service has a Dockerfile/.test(message) ? ' It has a Dockerfile, which runs where Docker is on in the sandbox (plan).' : '';
  return `No supported stack detected.${dockerfile}${list ? ` Supported stacks: ${list}.` : ''}`;
}

/** The builder that is the detected stack's own way: `node`, `python` or `stack` (null when no stack is detected). */
export function stackBuilderId(info) {
  const stack = info?.stack || null;
  if (!stack) { return null; }
  if (stack === 'python') { return 'python'; }
  return NODE_STACKS.includes(stack) ? 'node' : 'stack';
}

/** A builder of the service by id. */
export const builderById = (info, id) => (info?.builders || []).find((builder) => builder.id === id) || null;

/**
 * How the service runs now, from its `.flow/service.json` (`spec`, parsed) and its builders (`info`):
 * `{ id, option, port, command? }`. `id` 'custom' is a `start` the picker did not write (`command` is it); without a
 * file it is what runs when nothing is chosen (`recommended`).
 */
export function currentBuilder(spec, info) {
  const port = Number(spec?.port) > 0 ? Number(spec.port) : null;
  if (!spec) { return { id: info?.recommended || null, option: info?.option ?? '', port: null }; }
  if (isCliSpec(spec)) { return { id: 'cli', option: '', port: null }; }
  if (spec.run === 'device') { return { id: 'device', option: '', port: null }; }
  if (spec.run === 'docker') { return { id: `dockerfile:${spec.dockerfile || 'Dockerfile'}`, option: '', port }; }
  if (spec.run === 'compose') {
    const file = spec.compose || (info?.builders || []).find((builder) => builder.kind === 'compose')?.file || 'docker-compose.yml';
    return { id: `compose:${file}`, option: '', port };
  }
  const start = commandOf(spec.start);
  if (!start) { return { id: stackBuilderId(info), option: '', port: null }; }
  const make = start.match(/^make\s+(\S+)$/);
  if (make && SAFE_NAME.test(make[1])) { return { id: 'make', option: make[1], port }; }
  if (start === 'npm start') { return { id: 'node', option: 'start', port }; }
  const npm = start.match(/^npm run (\S+)$/);
  if (npm && SAFE_NAME.test(npm[1])) { return { id: 'node', option: npm[1], port }; }
  if (/^python3? manage\.py runserver/.test(start)) { return { id: 'python', option: 'manage.py', port }; }
  const python = start.match(/^python3? (\S+\.py)$/);
  if (python && SAFE_NAME.test(python[1])) { return { id: 'python', option: python[1], port }; }
  if (start === 'go run .') { return { id: 'go', option: 'run', port }; }
  return { id: 'custom', option: '', port, command: start, install: commandOf(spec.install), build: commandOf(spec.build) };
}

/** The `start` command of a code builder's option, or null (the stack its own way). Throws on an unsafe name. */
function startOf(kind, option) {
  if (!option) { return null; }
  if (!SAFE_NAME.test(option)) { throw new Error(`"${option}" cannot be run: use letters, digits and - _ . / only`); }
  if (kind === 'make') { return `make ${option}`; }
  if (kind === 'node') { return npmCommand(option); }
  if (kind === 'python') { return option === 'manage.py' ? 'python3 manage.py runserver 0.0.0.0:$PORT' : `python3 ${option}`; }
  if (kind === 'go') { return 'go run .'; }
  return null;
}

/**
 * The service.json after choosing `{ id, option, port }` (null when nothing is left: the file goes away and the runner
 * detects the service as before). The picker's fields are replaced, the others kept.
 */
export function specForBuilder(spec, { id, option = '', port = null }, info) {
  const builder = builderById(info, id);
  if (!builder) { throw new Error('This builder is not in the service any more'); }
  const { kind } = builder;
  if (kind === 'cli') {
    // Built only (vfs-service cliTool.js cliSpec): a worker with the stack's build and an idle start.
    const next = withoutRunSettings(spec);
    ['kind', 'health', 'build'].forEach((key) => { delete next[key]; });
    next.kind = 'worker';
    if (builder.build) { next.build = builder.build; }
    next.start = CLI_START;
    return next;
  }
  if (kind === 'device') {
    // A native app (vfs-service): nothing runs in the sandbox, so no start, install or build of its own.
    const next = withoutRunSettings(spec);
    ['install', 'build', 'kind', 'health'].forEach((key) => { delete next[key]; });
    next.run = 'device';
    return next;
  }
  const next = withoutRunSettings(spec);
  if (kind === 'dockerfile') {
    next.run = 'docker';
    next.dockerfile = builder.file;
  } else if (kind === 'compose') {
    next.run = 'compose';
    next.compose = builder.file;
  } else {
    const start = startOf(kind, option);
    if (start) {
      next.start = start;
    } else if (info?.composeApp) {
      // A compose file that builds the app would run instead of the stack (where Docker is on).
      next.run = 'stack';
    }
  }
  const wantsPort = PORT_KINDS.includes(kind) && (kind === 'dockerfile' || option);
  if (wantsPort && Number(port) > 0) { next.port = Number(port); }
  return Object.keys(next).filter((key) => key !== '$comment').length ? next : null;
}

/**
 * A compose service's preview entry point as `.flow/service.json` says it (`entry`: `{ service, port }`, or a name
 * alone, the runner's pick of its port): `{ service, port }` (`port` null for a name), or null.
 */
export function specEntryOf(spec) {
  const value = spec?.entry;
  if (typeof value === 'string') { return value.trim() ? { service: value.trim(), port: null } : null; }
  const service = typeof value?.service === 'string' ? value.service.trim() : '';
  if (!service) { return null; }
  const port = Number(value.port);
  return { service, port: Number.isInteger(port) && port > 0 && port < 65536 ? port : null };
}

/**
 * The service.json with a Docker Compose app's preview entry point (`{ service, port }`; without a valid port the
 * name alone; null removes it), the other fields kept. With no file yet (the compose file detected as the app),
 * `run: "compose"` on `file` is written with it: an `entry` alone is not a service.json the runner accepts.
 */
export function specWithEntry(spec, entry, file = null) {
  const next = { ...(spec || (file ? { run: 'compose', compose: file } : {})) };
  delete next.entry;
  const service = typeof entry?.service === 'string' ? entry.service.trim() : '';
  const port = Number(entry?.port);
  if (service) { next.entry = Number.isInteger(port) && port > 0 && port < 65536 ? { service, port } : service; }
  return next;
}

/** An option of a builder (by value), else its first. */
export const optionOf = (builder, value) => (builder?.options || []).find((item) => item.value === value) || builder?.options?.[0] || null;

/** Whether a builder's choice takes a port of its own (the stack's own way does not: the runner gives it PORT). */
export const takesPort = (builder, option) => Boolean(builder) && PORT_KINDS.includes(builder.kind) && (builder.kind === 'dockerfile' || Boolean(option));

/** The port to prefill for a builder's option: the one its file names (`EXPOSE`, a Python default), else none. */
export function portFor(builder, option) {
  if (!takesPort(builder, option)) { return null; }
  return optionOf(builder, option)?.port ?? builder.port ?? null;
}

/** The one-line command a builder's option runs, for the picker. */
export function commandLine(builder, option) {
  if (!builder) { return ''; }
  if (['dockerfile', 'compose', 'cli', 'device'].includes(builder.kind)) { return builder.command || ''; }
  const picked = optionOf(builder, option);
  if (!picked) { return ''; }
  if (builder.kind === 'node' && picked.value) { return `${npmCommand(picked.value)}${picked.command ? ` → ${picked.command}` : ''}`; }
  return picked.value ? (picked.command || picked.label) : picked.command;
}

/**
 * The builder in a few words, for the Overview card and the Apps list: "Makefile · make dev", "Dockerfile",
 * "Node.js · npm run dev", "Node.js · Vite dev server", "Docker Compose · .dev/docker-compose.yml".
 */
export function builderSummary(choice, info) {
  if (!choice?.id) { return null; }
  if (choice.id === 'custom') { return `Custom command · ${choice.command}`; }
  const builder = builderById(info, choice.id);
  if (choice.id === 'cli') { return `Command-line tool${builder?.tool ? ` · ${builder.tool}` : ''}`; }
  if (choice.id === 'device') { return builder?.label || info?.device?.label || 'Device app'; }
  const [kind, file] = choice.id.includes(':') ? choice.id.split(/:(.*)/s) : [builder?.kind || choice.id, null];
  if (kind === 'dockerfile') { return file === 'Dockerfile' ? 'Dockerfile' : `Dockerfile · ${file}`; }
  if (kind === 'compose') { return `Docker Compose · ${file}`; }
  if (kind === 'make') { return `Makefile · make ${choice.option}`; }
  const label = builder?.label || { node: 'Node.js', python: 'Python', go: 'Go' }[kind] || 'Detected stack';
  if (!choice.option) {
    const own = builder?.options?.find((item) => item.value === '');
    return own && !own.label.endsWith('(detected)') ? `${label} · ${own.label}` : label;
  }
  return `${label} · ${startOf(kind, choice.option) || choice.option}`.replace(' runserver 0.0.0.0:$PORT', ' runserver');
}

/** The builder summary of a service from vfs-service's `builders` entry (its `spec` included), or null. */
export const serviceBuilderSummary = (info) => (info ? builderSummary(currentBuilder(info.spec || null, info), info) : null);
